/**
 * ARGOV Connect — Meeting Detail
 * ARGOV-EVENTS-CONNECT-01 | Phase 1 + Phase 2
 *
 * Shows full meeting details: info, attendees, context links, invitation draft,
 * lifecycle actions (start / complete / cancel / reschedule / no-show), outcome.
 * Phase 2: NATIVE provider conference launch + guest invitation management.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Video, ExternalLink, Copy, CheckCircle2, XCircle,
  Clock, Users, Link2, ClipboardList, AlertCircle, RefreshCw,
  ListChecks, ChevronRight, Trash2, UserPlus, Ban, MonitorPlay,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  DRAFT:       "bg-slate-100 text-slate-700 border-slate-200",
  SCHEDULED:   "bg-blue-100 text-blue-700 border-blue-200",
  IN_PROGRESS: "bg-yellow-100 text-yellow-700 border-yellow-200",
  COMPLETED:   "bg-green-100 text-green-700 border-green-200",
  CANCELLED:   "bg-red-100 text-red-700 border-red-200",
  NO_SHOW:     "bg-orange-100 text-orange-700 border-orange-200",
};

const PROVIDER_LABELS = {
  GOOGLE_MEET: "Google Meet",
  ZOOM:        "Zoom",
  MS_TEAMS:    "Microsoft Teams",
  WEBEX:       "Cisco Webex",
  OTHER:       "External Link",
};

function formatDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <Card className="bg-white border border-slate-200" data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-indigo-500" />} {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export default function ConnectMeetingDetail() {
  const { mid }   = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const [meeting,  setMeeting]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [invitation, setInvitation] = useState(null);

  // Outcome form state
  const [showOutcome,    setShowOutcome]    = useState(false);
  const [outcomeNotes,   setOutcomeNotes]   = useState("");
  const [outcomeStatus,  setOutcomeStatus]  = useState("POSITIVE");
  const [actionItems,    setActionItems]    = useState([]);
  const [newActionItem,  setNewActionItem]  = useState("");
  const [decisions,      setDecisions]      = useState([]);
  const [newDecision,    setNewDecision]    = useState("");
  const [outcomeSaving,  setOutcomeSaving]  = useState(false);

  // Reschedule form
  const [showReschedule,  setShowReschedule]  = useState(false);
  const [newScheduledAt,  setNewScheduledAt]  = useState("");
  const [rescheduleReason,setRescheduleReason]= useState("");
  const [rescheduleSaving,setRescheduleSaving]= useState(false);

  // Phase 2 — Native Conference & Guest Invitations
  const [confInvitations,  setConfInvitations] = useState([]);
  const [confInvLoading,   setConfInvLoading]  = useState(false);
  const [confInvLoaded,    setConfInvLoaded]   = useState(false);
  const [showInviteForm,   setShowInviteForm]  = useState(false);
  const [inviteeName,      setInviteeName]     = useState("");
  const [inviteeEmail,     setInviteeEmail]    = useState("");
  const [creatingInvite,   setCreatingInvite]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/connect/meetings/${mid}`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMeeting(await r.json());
    } catch (e) {
      toast.error("Failed to load meeting");
    } finally {
      setLoading(false);
    }
  }, [mid]);

  useEffect(() => { load(); }, [load]);

  const loadInvitation = async () => {
    const r = await fetch(`${API}/api/connect/meetings/${mid}/invitation`, { credentials: "include" });
    if (r.ok) setInvitation(await r.json());
  };

  const action = async (endpoint, method = "POST", body = undefined) => {
    const r = await fetch(`${API}/api/connect/meetings/${mid}/${endpoint}`, {
      method,
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err?.detail?.message || err?.detail || `HTTP ${r.status}`);
    }
    return r.json();
  };

  const handleStart = async () => {
    try {
      await action("start");
      toast.success("Meeting started");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel this meeting?")) return;
    try {
      await action("cancel", "POST", { reason: "" });
      toast.success("Meeting cancelled");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleNoShow = async () => {
    try {
      await action("no-show");
      toast.success("Marked as no-show");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleComplete = async () => {
    if (!outcomeNotes.trim()) return toast.error("Notes are required");
    setOutcomeSaving(true);
    try {
      await action("complete", "POST", {
        outcome: {
          notes:                 outcomeNotes,
          outcome_status:        outcomeStatus,
          decisions:             decisions.filter(Boolean),
          action_items:          actionItems.filter(Boolean),
          attendee_count_actual: 0,
        },
      });
      toast.success("Meeting completed");
      setShowOutcome(false);
      setActionItems([]);
      setDecisions([]);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setOutcomeSaving(false); }
  };

  const handleReschedule = async () => {
    if (!newScheduledAt) return toast.error("New date/time required");
    setRescheduleSaving(true);
    try {
      await action("reschedule", "POST", { scheduled_at: newScheduledAt, reason: rescheduleReason });
      toast.success("Meeting rescheduled");
      setShowReschedule(false);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setRescheduleSaving(false); }
  };

  const copyInvitation = () => {
    if (!invitation?.invitation_draft) return;
    navigator.clipboard.writeText(invitation.invitation_draft)
      .then(() => toast.success("Invitation copied to clipboard"))
      .catch(() => toast.error("Clipboard not available"));
  };

  // ── Phase 2: Conference invitation handlers ────────────────────────────────
  const loadConferenceInvitations = useCallback(async () => {
    if (!mid) return;
    setConfInvLoading(true);
    try {
      const r = await fetch(`${API}/api/connect/meetings/${mid}/invitations`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setConfInvitations(d.results || []);
        setConfInvLoaded(true);
      }
    } catch { /* silent */ }
    finally { setConfInvLoading(false); }
  }, [mid]);

  const handleCreateConferenceInvite = async (e) => {
    e.preventDefault();
    if (!inviteeName.trim()) return toast.error("Guest name is required");
    setCreatingInvite(true);
    try {
      const r = await fetch(`${API}/api/connect/meetings/${mid}/invitations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invited_name: inviteeName.trim(), invited_email: inviteeEmail.trim() || undefined }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail?.message || d?.detail || `HTTP ${r.status}`);
      }
      toast.success("Guest invite link created");
      setInviteeName("");
      setInviteeEmail("");
      setShowInviteForm(false);
      await loadConferenceInvitations();
    } catch (err) { toast.error(err.message); }
    finally { setCreatingInvite(false); }
  };

  const handleRevokeInvite = async (token) => {
    if (!window.confirm("Revoke this guest invite? If the guest is in the meeting they will be removed immediately.")) return;
    try {
      const r = await fetch(`${API}/api/connect/invitations/${token}/revoke`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "Revoked by host" }),
      });
      if (r.ok) { toast.success("Invitation revoked"); await loadConferenceInvitations(); }
      else toast.error("Could not revoke invitation");
    } catch { toast.error("Network error"); }
  };

  const copyGuestLink = (token) => {
    const url = `${window.location.origin}/guest/join/${token}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Guest link copied to clipboard"))
      .catch(() => toast.error("Clipboard not available"));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-24 text-slate-400" data-testid="connect-detail-loading">
      Loading…
    </div>
  );
  if (!meeting) return (
    <div className="text-center py-24 text-slate-400" data-testid="connect-detail-not-found">
      Meeting not found.
    </div>
  );

  const isActive   = ["DRAFT", "SCHEDULED", "IN_PROGRESS"].includes(meeting.status);
  const isTerminal = ["COMPLETED", "CANCELLED", "NO_SHOW"].includes(meeting.status);
  const caps = user?.capabilities || [];
  const canUpdate  = caps.includes("meeting.update")  || caps.includes("meeting.*");
  const canCancel  = caps.includes("meeting.cancel")  || caps.includes("meeting.*");
  const canOutcome = caps.includes("meeting.outcome.manage") || caps.includes("meeting.*");

  // Phase 2 capability checks
  const isNative       = meeting.provider === "NATIVE";
  const canJoinConf    = caps.includes("conference.join")         || caps.includes("conference.*");
  const canHostConf    = caps.includes("conference.host")         || caps.includes("conference.*");
  const canInviteGuest = caps.includes("conference.invite.guest") || caps.includes("conference.*");

  return (
    <div className="max-w-3xl mx-auto space-y-5 p-1" data-testid="connect-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            size="sm"
            data-testid="connect-detail-back-btn"
            onClick={() => navigate("/connect/meetings")}
            className="mt-0.5"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-slate-900" data-testid="connect-detail-title">
                {meeting.title}
              </h1>
              <Badge
                className={`text-xs ${STATUS_COLORS[meeting.status]}`}
                data-testid="connect-detail-status"
              >
                {meeting.status}
              </Badge>
            </div>
            <p className="text-xs text-slate-400 mt-0.5" data-testid="connect-detail-ref">
              {meeting.meeting_ref} · {PROVIDER_LABELS[meeting.provider] || meeting.provider}
            </p>
          </div>
        </div>

        {/* Primary action: Join */}
        {isActive && (
          <>
            {isNative && canJoinConf ? (
              <Button
                data-testid="connect-detail-launch-conference-btn"
                className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white flex-shrink-0"
                onClick={() => navigate(`/connect/meetings/${mid}/conference`)}
              >
                <MonitorPlay className="w-4 h-4" /> Launch Conference
              </Button>
            ) : !isNative ? (
              <Button
                data-testid="connect-detail-join-btn"
                className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white flex-shrink-0"
                onClick={() => window.open(meeting.meeting_url, "_blank", "noopener")}
              >
                <ExternalLink className="w-4 h-4" /> Join Meeting
              </Button>
            ) : null}
          </>
        )}
      </div>

      {/* Meeting Info */}
      <Section title="Meeting Information" icon={Video} testId="connect-detail-info-section">
        <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <div>
            <p className="text-xs text-slate-400 font-medium">Scheduled</p>
            <p className="text-slate-700 mt-0.5" data-testid="connect-detail-scheduled">
              {formatDT(meeting.scheduled_at)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 font-medium">Duration</p>
            <p className="text-slate-700 mt-0.5">{meeting.duration_minutes} minutes</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 font-medium">Timezone</p>
            <p className="text-slate-700 mt-0.5">{meeting.timezone}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 font-medium">Type</p>
            <p className="text-slate-700 mt-0.5">{meeting.meeting_type?.replace("_", " ")}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 font-medium">Host</p>
            <p className="text-slate-700 mt-0.5" data-testid="connect-detail-host">{meeting.host_name}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 font-medium">Phase</p>
            <p className="text-slate-500 mt-0.5 text-xs font-mono">{meeting.phase}</p>
          </div>
          {meeting.description && (
            <div className="col-span-2">
              <p className="text-xs text-slate-400 font-medium">Description</p>
              <p className="text-slate-700 mt-0.5">{meeting.description}</p>
            </div>
          )}
          <div className="col-span-2">
            <p className="text-xs text-slate-400 font-medium mb-1">Meeting Link</p>
            <a
              href={meeting.meeting_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="connect-detail-url"
              className="text-indigo-600 hover:underline text-xs font-mono break-all"
            >
              {meeting.meeting_url}
            </a>
          </div>
        </div>
      </Section>

      {/* Context References */}
      {meeting.context_refs?.length > 0 && (
        <Section title="Context Links" icon={Link2} testId="connect-detail-context-section">
          <div className="space-y-2">
            {meeting.context_refs.map((ref, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                <Badge variant="outline" className="text-xs font-mono">{ref.entity_type}</Badge>
                <span className="text-sm text-slate-700">{ref.entity_label}</span>
                <span className="text-xs text-slate-400 font-mono">{ref.entity_id}</span>
                {ref.validated && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-auto" />
                )}
                {/* CRM deep-link for linked entities */}
                {(ref.entity_type === "LEAD" || ref.entity_type === "OPPORTUNITY" || ref.entity_type === "CUSTOMER") && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto text-xs h-6 gap-1 text-indigo-600 hover:text-indigo-800"
                    data-testid={`connect-crm-link-${ref.entity_id}`}
                    onClick={() => navigate(
                      ref.entity_type === "LEAD"        ? `/crm/leads/${ref.entity_id}` :
                      ref.entity_type === "OPPORTUNITY" ? `/crm/opportunities/${ref.entity_id}` :
                                                         `/crm/customers/${ref.entity_id}`
                    )}
                  >
                    View CRM <ChevronRight className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Attendees */}
      {(meeting.attendees?.length > 0) && (
        <Section title={`Attendees (${meeting.attendees.length})`} icon={Users} testId="connect-detail-attendees-section">
          <div className="space-y-2">
            {meeting.attendees.map((a, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold">
                  {a.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{a.name}</p>
                  {a.email && <p className="text-xs text-slate-400 truncate">{a.email}</p>}
                </div>
                <Badge variant="outline" className="text-xs">{a.role}</Badge>
                <Badge className={`text-xs ${a.attend_status === "ATTENDED" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                  {a.attend_status}
                </Badge>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Phase 2: ARGOV Native Conference Section (NATIVE provider only) ── */}
      {isNative && (
        <Card className="bg-white border border-indigo-200" data-testid="connect-detail-conference-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Video className="w-4 h-4 text-indigo-500" />
              ARGOV Native Conference
              <Badge className="ml-auto text-xs bg-indigo-50 text-indigo-700 border border-indigo-200">
                Phase 2
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">

            {/* Launch block */}
            <div className="flex items-start justify-between gap-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-800">
                  {meeting.conference_room_name ? "Conference room active" : "Conference room not started"}
                </p>
                <p className="text-xs text-slate-500">
                  {canJoinConf
                    ? "Authenticated participants join with full camera, microphone and screen-share controls."
                    : "You do not have conference.join permission for this meeting."}
                </p>
              </div>
              {isActive && canJoinConf && (
                <Button
                  data-testid="connect-conference-launch-btn"
                  className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white flex-shrink-0 text-xs"
                  onClick={() => navigate(`/connect/meetings/${mid}/conference`)}
                >
                  <MonitorPlay className="w-3.5 h-3.5" /> Launch
                </Button>
              )}
            </div>

            {/* Guest Invitations (conference.invite.guest only) */}
            {canInviteGuest && (
              <div data-testid="connect-conference-invitations-block">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
                      Guest Invitations
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Single-use, revocable links — no platform login needed for guests.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!confInvLoaded && (
                      <Button
                        size="sm" variant="outline"
                        data-testid="connect-load-invitations-btn"
                        className="text-xs gap-1"
                        onClick={loadConferenceInvitations}
                        disabled={confInvLoading}
                      >
                        {confInvLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Load
                      </Button>
                    )}
                    {isActive && (
                      <Button
                        size="sm"
                        data-testid="connect-create-invite-btn"
                        className="text-xs gap-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => { setShowInviteForm(v => !v); if (!confInvLoaded) loadConferenceInvitations(); }}
                      >
                        <UserPlus className="w-3 h-3" /> New Invite
                      </Button>
                    )}
                  </div>
                </div>

                {/* Create invite form */}
                {showInviteForm && isActive && (
                  <form
                    onSubmit={handleCreateConferenceInvite}
                    className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 space-y-3"
                    data-testid="connect-invite-form"
                  >
                    <p className="text-xs font-semibold text-slate-600">Create Guest Invite Link</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-600">Guest Name *</Label>
                        <Input
                          data-testid="connect-invite-name-input"
                          placeholder="e.g. Priya Sharma"
                          value={inviteeName}
                          onChange={e => setInviteeName(e.target.value)}
                          className="mt-1 h-8 text-xs bg-white"
                          autoFocus
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Email (optional)</Label>
                        <Input
                          data-testid="connect-invite-email-input"
                          type="email"
                          placeholder="guest@example.com"
                          value={inviteeEmail}
                          onChange={e => setInviteeEmail(e.target.value)}
                          className="mt-1 h-8 text-xs bg-white"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        size="sm"
                        data-testid="connect-invite-submit-btn"
                        disabled={creatingInvite}
                        className="text-xs bg-green-600 hover:bg-green-700 text-white"
                      >
                        {creatingInvite ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Creating…</> : "Generate Link"}
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowInviteForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                {/* Invitations list */}
                {confInvLoaded && (
                  <div data-testid="connect-invitations-list" className="space-y-2">
                    {confInvitations.length === 0 ? (
                      <p className="text-xs text-slate-400 py-2">No guest invitations yet.</p>
                    ) : (
                      confInvitations.map(inv => (
                        <div
                          key={inv.id}
                          className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2.5 bg-white"
                          data-testid={`connect-invitation-row-${inv.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-800 truncate">{inv.invited_name}</span>
                              <Badge className={`text-xs flex-shrink-0 ${
                                inv.status === "PENDING"  ? "bg-blue-100 text-blue-700 border-blue-200" :
                                inv.status === "REDEEMED" ? "bg-green-100 text-green-700 border-green-200" :
                                inv.status === "REVOKED"  ? "bg-red-100 text-red-700 border-red-200" :
                                inv.status === "EXPIRED"  ? "bg-slate-100 text-slate-500 border-slate-200" :
                                "bg-slate-100 text-slate-600"
                              }`}>
                                {inv.status}
                              </Badge>
                            </div>
                            {inv.invited_email && (
                              <p className="text-xs text-slate-400 truncate mt-0.5">{inv.invited_email}</p>
                            )}
                            {inv.expires_at && (
                              <p className="text-xs text-slate-400 mt-0.5">
                                Expires: {formatDT(inv.expires_at)}
                              </p>
                            )}
                          </div>
                          {inv.status === "PENDING" && (
                            <div className="flex gap-1.5 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs px-2 gap-1"
                                data-testid={`connect-invite-copy-${inv.id}`}
                                onClick={() => copyGuestLink(inv.id)}
                              >
                                <Copy className="w-3 h-3" /> Copy Link
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs px-2 gap-1 text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`connect-invite-revoke-${inv.id}`}
                                onClick={() => handleRevokeInvite(inv.id)}
                              >
                                <Ban className="w-3 h-3" /> Revoke
                              </Button>
                            </div>
                          )}
                          {inv.status === "REDEEMED" && inv.admitted_at && (
                            <Badge className="text-xs bg-green-50 text-green-700 flex-shrink-0 gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Admitted
                            </Badge>
                          )}
                        </div>
                      ))
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs gap-1 text-slate-400 hover:text-slate-600"
                      data-testid="connect-invitations-refresh-btn"
                      onClick={loadConferenceInvitations}
                      disabled={confInvLoading}
                    >
                      <RefreshCw className={`w-3 h-3 ${confInvLoading ? "animate-spin" : ""}`} />
                      Refresh
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invitation Draft */}
      <Section title="Invitation Draft" icon={ClipboardList} testId="connect-detail-invitation-section">        <p className="text-xs text-slate-400 mb-3">
          Copy and share this draft manually. No automatic emails are sent — Phase 1.
        </p>
        {!invitation ? (
          <Button
            variant="outline"
            size="sm"
            data-testid="connect-load-invitation-btn"
            onClick={loadInvitation}
            className="text-xs gap-1"
          >
            <ClipboardList className="w-3.5 h-3.5" /> Load Invitation Draft
          </Button>
        ) : (
          <div>
            <pre
              data-testid="connect-invitation-text"
              className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed"
            >
              {invitation.invitation_draft}
            </pre>
            <Button
              size="sm"
              variant="outline"
              data-testid="connect-copy-invitation-btn"
              onClick={copyInvitation}
              className="mt-2 gap-1 text-xs"
            >
              <Copy className="w-3.5 h-3.5" /> Copy to Clipboard
            </Button>
          </div>
        )}
      </Section>

      {/* Outcome (if completed) */}
      {meeting.status === "COMPLETED" && meeting.outcome && (
        <Section title="Meeting Outcome" icon={CheckCircle2} testId="connect-detail-outcome-section">
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={`text-xs ${
                meeting.outcome.outcome_status === "POSITIVE"       ? "bg-green-100 text-green-700"  :
                meeting.outcome.outcome_status === "NEGATIVE"       ? "bg-red-100 text-red-700"      :
                meeting.outcome.outcome_status === "NEEDS_FOLLOW_UP"? "bg-amber-100 text-amber-700"  :
                "bg-slate-100 text-slate-700"
              }`} data-testid="connect-detail-outcome-status">
                {meeting.outcome.outcome_status}
              </Badge>
              <span className="text-xs text-slate-400">
                Recorded by {meeting.outcome.recorded_by_name}
              </span>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="connect-detail-outcome-notes">
              {meeting.outcome.notes}
            </p>
            {meeting.outcome.decisions?.length > 0 && (
              <div data-testid="connect-detail-decisions">
                <p className="text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Decisions</p>
                <ul className="space-y-1">
                  {meeting.outcome.decisions.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {meeting.outcome.action_items?.length > 0 && (
              <div data-testid="connect-detail-action-items">
                <p className="text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Follow-up Action Items</p>
                <ul className="space-y-1">
                  {meeting.outcome.action_items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <ListChecks className="w-3.5 h-3.5 text-indigo-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Actions */}
      {isActive && (
        <Card className="bg-white border border-slate-200" data-testid="connect-detail-actions-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {meeting.status === "SCHEDULED" && canUpdate && (
              <Button
                size="sm"
                variant="outline"
                data-testid="connect-action-start-btn"
                onClick={handleStart}
                className="gap-1 text-xs"
              >
                <Clock className="w-3.5 h-3.5" /> Mark In Progress
              </Button>
            )}

            {canUpdate && (
              <Button
                size="sm"
                variant="outline"
                data-testid="connect-action-reschedule-btn"
                onClick={() => setShowReschedule(r => !r)}
                className="gap-1 text-xs"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Reschedule
              </Button>
            )}

            {canOutcome && meeting.status !== "DRAFT" && (
              <Button
                size="sm"
                variant="outline"
                data-testid="connect-action-complete-btn"
                onClick={() => setShowOutcome(o => !o)}
                className="gap-1 text-xs text-green-700 border-green-300 hover:bg-green-50"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Record Outcome
              </Button>
            )}

            {canOutcome && meeting.status !== "DRAFT" && (
              <Button
                size="sm"
                variant="ghost"
                data-testid="connect-action-noshow-btn"
                onClick={handleNoShow}
                className="gap-1 text-xs text-orange-600 hover:bg-orange-50"
              >
                <AlertCircle className="w-3.5 h-3.5" /> No-Show
              </Button>
            )}

            {canCancel && (
              <Button
                size="sm"
                variant="ghost"
                data-testid="connect-action-cancel-btn"
                onClick={handleCancel}
                className="gap-1 text-xs text-red-600 hover:bg-red-50"
              >
                <XCircle className="w-3.5 h-3.5" /> Cancel
              </Button>
            )}
          </CardContent>

          {/* Reschedule form */}
          {showReschedule && (
            <CardContent className="pt-0 border-t border-slate-100" data-testid="connect-reschedule-form">
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-600">New Date & Time *</Label>
                    <Input
                      type="datetime-local"
                      value={newScheduledAt}
                      onChange={e => setNewScheduledAt(e.target.value)}
                      className="mt-1 h-8 text-xs"
                      data-testid="connect-reschedule-datetime"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Reason</Label>
                    <Input
                      placeholder="Optional reason"
                      value={rescheduleReason}
                      onChange={e => setRescheduleReason(e.target.value)}
                      className="mt-1 h-8 text-xs"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    data-testid="connect-reschedule-confirm-btn"
                    disabled={rescheduleSaving}
                    onClick={handleReschedule}
                    className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {rescheduleSaving ? "Saving…" : "Confirm Reschedule"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowReschedule(false)} className="text-xs">
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          )}

          {/* Outcome form */}
          {showOutcome && (
            <CardContent className="pt-0 border-t border-slate-100" data-testid="connect-outcome-form">
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Outcome Status</Label>
                    <Select value={outcomeStatus} onValueChange={setOutcomeStatus}>
                      <SelectTrigger className="mt-1 h-8 text-xs" data-testid="connect-outcome-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="POSITIVE">Positive</SelectItem>
                        <SelectItem value="NEUTRAL">Neutral</SelectItem>
                        <SelectItem value="NEGATIVE">Negative</SelectItem>
                        <SelectItem value="NEEDS_FOLLOW_UP">Needs Follow-up</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600">Meeting Notes *</Label>
                  <Textarea
                    data-testid="connect-outcome-notes"
                    placeholder="Record what was discussed, decided and agreed…"
                    value={outcomeNotes}
                    onChange={e => setOutcomeNotes(e.target.value)}
                    className="mt-1 text-xs resize-none"
                    rows={3}
                  />
                </div>
                {/* Decisions */}
                <div>
                  <Label className="text-xs font-medium text-slate-600">Decisions Made</Label>
                  <div className="mt-1 space-y-1">
                    {decisions.map((d, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <span className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-700">{d}</span>
                        <button type="button" onClick={() => setDecisions(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-1.5">
                      <Input
                        data-testid="connect-outcome-decision-input"
                        placeholder="Add a decision…"
                        value={newDecision}
                        onChange={e => setNewDecision(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter" && newDecision.trim()) { setDecisions(p => [...p, newDecision.trim()]); setNewDecision(""); e.preventDefault(); }}}
                        className="h-7 text-xs flex-1"
                      />
                      <Button
                        type="button" size="sm" variant="outline"
                        data-testid="connect-outcome-add-decision-btn"
                        className="h-7 text-xs px-2"
                        onClick={() => { if (newDecision.trim()) { setDecisions(p => [...p, newDecision.trim()]); setNewDecision(""); }}}
                      >+ Add</Button>
                    </div>
                  </div>
                </div>
                {/* Action Items / Follow-up Tasks */}
                <div>
                  <Label className="text-xs font-medium text-slate-600">Follow-up Action Items</Label>
                  <div className="mt-1 space-y-1">
                    {actionItems.map((item, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <span className="flex-1 text-xs bg-indigo-50 border border-indigo-100 rounded px-2 py-1 text-slate-700">{item}</span>
                        <button type="button" onClick={() => setActionItems(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-1.5">
                      <Input
                        data-testid="connect-outcome-action-input"
                        placeholder="Add follow-up task…"
                        value={newActionItem}
                        onChange={e => setNewActionItem(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter" && newActionItem.trim()) { setActionItems(p => [...p, newActionItem.trim()]); setNewActionItem(""); e.preventDefault(); }}}
                        className="h-7 text-xs flex-1"
                      />
                      <Button
                        type="button" size="sm" variant="outline"
                        data-testid="connect-outcome-add-action-btn"
                        className="h-7 text-xs px-2"
                        onClick={() => { if (newActionItem.trim()) { setActionItems(p => [...p, newActionItem.trim()]); setNewActionItem(""); }}}
                      >+ Add</Button>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    data-testid="connect-outcome-submit-btn"
                    disabled={outcomeSaving}
                    onClick={handleComplete}
                    className="text-xs bg-green-600 hover:bg-green-700 text-white"
                  >
                    {outcomeSaving ? "Saving…" : "Mark Completed"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowOutcome(false)} className="text-xs">
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Audit Log */}
      {meeting.audit_log?.length > 0 && (
        <Section title="Audit Log" icon={ClipboardList} testId="connect-detail-audit-section">
          <div className="space-y-1">
            {[...meeting.audit_log].reverse().map((e, i) => (
              <div key={i} className="flex items-start gap-2 text-xs py-1.5 border-b border-slate-100 last:border-0">
                <Badge variant="outline" className="text-xs font-mono flex-shrink-0">{e.action}</Badge>
                <span className="text-slate-600 flex-1">{e.note}</span>
                <span className="text-slate-400 whitespace-nowrap">
                  {e.by_name} · {formatDT(e.at)}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
